/**
 * useEdgeShapePointDrag - Hook para drag de vértices internos de edge
 *
 * @version 1.0
 * @created 2026-04-11
 * @module core/spatial/hooks
 *
 * @description
 * Permite arrastrar los vértices internos (shape points) de un edge
 * sin modificar su topología.
 *
 * - Solo afecta geometry.coordinates[1..n-2]
 * - NO afecta extremos (fromNode, toNode)
 * - NO afecta conectividad
 * - NO afecta sintaxis espacial
 *
 * @example
 * ```typescript
 * useEdgeShapePointDrag({
 *   enabled: state.geometryEditEdgeId !== null,
 *   onDragStart: (index) => startShapePointDrag(index),
 *   onDragMove: (pos) => updateShapePointPosition(pos),
 *   onDragEnd: () => confirmGeometryEdit(),
 * });
 * ```
 */

import { useEffect, useRef } from 'react';
import type { MapMouseEvent } from 'maplibre-gl';
import { mapEngine } from '../../map-engine/MapEngineService';

const SHAPE_HANDLES_LAYER_ID = 'edge-shape-handles';

export interface UseEdgeShapePointDragConfig {
  /** Si el drag está habilitado */
  enabled: boolean;
  /** Callback cuando empieza el drag de un shape point */
  onDragStart: (shapePointIndex: number) => void;
  /** Callback durante el drag con nueva posición */
  onDragMove: (newPosition: [number, number]) => void;
  /** Callback cuando termina el drag */
  onDragEnd: () => void;
  /** Callback para cancelar drag (ESC) */
  onDragCancel?: () => void;
}

/**
 * Hook para drag de shape points internos de edge
 */
export function useEdgeShapePointDrag({
  enabled,
  onDragStart,
  onDragMove,
  onDragEnd,
  onDragCancel,
}: UseEdgeShapePointDragConfig): void {
  const isDraggingRef = useRef(false);
  const draggedIndexRef = useRef<number | null>(null);

  // Refs para callbacks - evita re-registro por cambio de referencias
  const onDragStartRef = useRef(onDragStart);
  const onDragMoveRef = useRef(onDragMove);
  const onDragEndRef = useRef(onDragEnd);
  const onDragCancelRef = useRef(onDragCancel);

  // Mantener refs sincronizados
  useEffect(() => { onDragStartRef.current = onDragStart; }, [onDragStart]);
  useEffect(() => { onDragMoveRef.current = onDragMove; }, [onDragMove]);
  useEffect(() => { onDragEndRef.current = onDragEnd; }, [onDragEnd]);
  useEffect(() => { onDragCancelRef.current = onDragCancel; }, [onDragCancel]);

  useEffect(() => {
    console.log('[useEdgeShapePointDrag] 🎬 useEffect running, enabled:', enabled);

    if (!enabled) {
      console.log('[useEdgeShapePointDrag] ⏭️ Disabled, skipping listener registration');
      return;
    }

    const map = mapEngine.getMap();
    if (!map) {
      console.log('[useEdgeShapePointDrag] ⚠️ Map not available');
      return;
    }

    // Verificar si la layer existe
    const layerExists = !!map.getLayer(SHAPE_HANDLES_LAYER_ID);
    console.log('[useEdgeShapePointDrag] ✅ Registering listeners, layer exists:', layerExists);

    // ══════════════════════════════════════════════════════════════════════
    // MOUSEDOWN: Detectar inicio de drag en shape point
    // ══════════════════════════════════════════════════════════════════════
    const handleMouseDown = (e: MapMouseEvent): void => {
      // DEBUG: Log CADA mousedown para saber si el handler corre
      console.log('[useEdgeShapePointDrag] 🖱️ mousedown event at:', e.point);

      // Buscar shape points bajo el cursor
      const features = map.queryRenderedFeatures(e.point, {
        layers: [SHAPE_HANDLES_LAYER_ID],
      });

      console.log('[useEdgeShapePointDrag] 🔍 queryRenderedFeatures result:', {
        layerId: SHAPE_HANDLES_LAYER_ID,
        featuresFound: features.length,
        features: features.map(f => ({
          id: f.id,
          properties: f.properties,
        })),
      });

      if (features.length > 0) {
        const feature = features[0];
        const shapePointIndex = feature.properties?.shapePointIndex as number;

        console.log('[useEdgeShapePointDrag] 🎯 Handle detected:', {
          shapePointIndex,
          featureId: feature.id,
          properties: feature.properties,
        });

        if (typeof shapePointIndex === 'number') {
          console.log('[useEdgeShapePointDrag] ✅ Starting drag on shape point:', shapePointIndex);

          isDraggingRef.current = true;
          draggedIndexRef.current = shapePointIndex;

          onDragStartRef.current(shapePointIndex);

          // Cambiar cursor
          map.getCanvas().style.cursor = 'grabbing';

          // Prevenir pan del mapa
          map.dragPan.disable();

          // Prevenir propagación
          e.preventDefault();
        } else {
          console.log('[useEdgeShapePointDrag] ⚠️ shapePointIndex is not a number:', typeof shapePointIndex);
        }
      }
    };

    // ══════════════════════════════════════════════════════════════════════
    // MOUSEMOVE: Actualizar posición durante drag
    // ══════════════════════════════════════════════════════════════════════
    const handleMouseMove = (e: MapMouseEvent): void => {
      if (!isDraggingRef.current || draggedIndexRef.current === null) return;

      const newPosition: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      onDragMoveRef.current(newPosition);
    };

    // ══════════════════════════════════════════════════════════════════════
    // MOUSEUP: Finalizar drag
    // ══════════════════════════════════════════════════════════════════════
    const handleMouseUp = (_e: MapMouseEvent): void => {
      if (!isDraggingRef.current) return;

      console.log('[useEdgeShapePointDrag] 💾 Drag ended on shape point:', draggedIndexRef.current);

      // Limpiar estado
      isDraggingRef.current = false;
      draggedIndexRef.current = null;

      // Restaurar cursor y pan
      map.getCanvas().style.cursor = '';
      map.dragPan.enable();

      // Notificar fin de drag
      onDragEndRef.current();
    };

    // ══════════════════════════════════════════════════════════════════════
    // KEYDOWN: Cancelar con ESC
    // ══════════════════════════════════════════════════════════════════════
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && isDraggingRef.current) {
        console.log('[useEdgeShapePointDrag] ❌ Drag cancelled via ESC');

        isDraggingRef.current = false;
        draggedIndexRef.current = null;

        map.getCanvas().style.cursor = '';
        map.dragPan.enable();

        if (onDragCancelRef.current) {
          onDragCancelRef.current();
        }
      }
    };

    // ══════════════════════════════════════════════════════════════════════
    // EVENTOS - Registrar listeners
    // ══════════════════════════════════════════════════════════════════════
    console.log('[useEdgeShapePointDrag] 📝 Registering mousedown listener on map');
    map.on('mousedown', handleMouseDown);
    map.on('mousemove', handleMouseMove);
    map.on('mouseup', handleMouseUp);
    document.addEventListener('keydown', handleKeyDown);

    // También escuchar mouseup fuera del mapa
    const handleDocumentMouseUp = (): void => {
      if (isDraggingRef.current) {
        handleMouseUp({ lngLat: { lng: 0, lat: 0 } } as any);
      }
    };
    document.addEventListener('mouseup', handleDocumentMouseUp);

    console.log('[useEdgeShapePointDrag] ✅ All listeners registered');

    // ══════════════════════════════════════════════════════════════════════
    // CLEANUP
    // ══════════════════════════════════════════════════════════════════════
    return () => {
      console.log('[useEdgeShapePointDrag] 🧹 Cleanup - removing listeners');
      map.off('mousedown', handleMouseDown);
      map.off('mousemove', handleMouseMove);
      map.off('mouseup', handleMouseUp);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mouseup', handleDocumentMouseUp);

      // Limpiar estado si estaba en drag
      if (isDraggingRef.current) {
        map.getCanvas().style.cursor = '';
        map.dragPan.enable();
        isDraggingRef.current = false;
        draggedIndexRef.current = null;
      }
      console.log('[useEdgeShapePointDrag] ✅ Cleanup complete');
    };
  }, [enabled]); // Solo depende de enabled, los callbacks usan refs
}
